import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Map, Copy, Download, Plus, Trash2, Globe, Calendar, Settings } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface SitemapUrl {
  loc: string;
  lastmod: string;
  changefreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority: string;
}

interface SitemapConfig {
  urls: SitemapUrl[];
  domain: string;
  includeImages: boolean;
  includeVideos: boolean;
  defaultChangefreq: string;
  defaultPriority: string;
}

const SitemapGenerator: React.FC = () => {
  const [config, setConfig] = useState<SitemapConfig>({
    urls: [
      {
        loc: '/',
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'daily',
        priority: '1.0'
      }
    ],
    domain: '',
    includeImages: false,
    includeVideos: false,
    defaultChangefreq: 'weekly',
    defaultPriority: '0.8'
  });

  const [generatedSitemap, setGeneratedSitemap] = useState('');
  const [bulkUrls, setBulkUrls] = useState('');

  useSEO({
    title: 'XML Sitemap Generator - Create SEO Sitemaps for Search Engines',
    description: 'Generate XML sitemaps to help search engines discover and index your website content. Create sitemaps with custom priorities, change frequencies, and last modified dates.',
    keywords: 'XML sitemap generator, sitemap xml, SEO sitemap, search engine sitemap, website sitemap',
    canonical: 'https://snap-tools.online/seo/sitemap-generator'
  });

  // Add new URL
  const addUrl = () => {
    setConfig(prev => ({
      ...prev,
      urls: [...prev.urls, {
        loc: '',
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: prev.defaultChangefreq as any,
        priority: prev.defaultPriority
      }]
    }));
  };

  // Remove URL
  const removeUrl = (index: number) => {
    if (config.urls.length > 1) {
      setConfig(prev => ({
        ...prev,
        urls: prev.urls.filter((_, i) => i !== index)
      }));
    }
  };

  // Update URL
  const updateUrl = (index: number, field: keyof SitemapUrl, value: string) => {
    setConfig(prev => ({
      ...prev,
      urls: prev.urls.map((url, i) => 
        i === index ? { ...url, [field]: value } : url
      )
    }));
  };

  // Add bulk URLs
  const addBulkUrls = () => {
    if (!bulkUrls.trim()) return;

    const urlList = bulkUrls
      .split('\n')
      .map(url => url.trim())
      .filter(url => url && url.length > 0)
      .map(url => ({
        loc: url.startsWith('/') ? url : `/${url}`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: config.defaultChangefreq as any,
        priority: config.defaultPriority
      }));

    setConfig(prev => ({
      ...prev,
      urls: [...prev.urls, ...urlList]
    }));

    setBulkUrls('');
    toast.success(`Added ${urlList.length} URLs to sitemap`);
  };

  // Generate common pages
  const generateCommonPages = () => {
    const commonPages = [
      { loc: '/', priority: '1.0', changefreq: 'daily' },
      { loc: '/about', priority: '0.8', changefreq: 'monthly' },
      { loc: '/contact', priority: '0.7', changefreq: 'monthly' },
      { loc: '/services', priority: '0.9', changefreq: 'weekly' },
      { loc: '/products', priority: '0.9', changefreq: 'weekly' },
      { loc: '/blog', priority: '0.8', changefreq: 'daily' },
      { loc: '/privacy', priority: '0.5', changefreq: 'yearly' },
      { loc: '/terms', priority: '0.5', changefreq: 'yearly' }
    ];

    const newUrls = commonPages.map(page => ({
      loc: page.loc,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: page.changefreq as any,
      priority: page.priority
    }));

    setConfig(prev => ({
      ...prev,
      urls: newUrls
    }));

    toast.success('Common pages added to sitemap');
  };

  // Generate XML sitemap
  const generateSitemap = () => {
    if (!config.domain) {
      toast.error('Please enter your domain name');
      return;
    }

    if (config.urls.length === 0) {
      toast.error('Please add at least one URL');
      return;
    }

    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"';
    
    if (config.includeImages) {
      xml += ' xmlns:image="http://www.google.com/schemas/sitemap-image/1.1"';
    }
    
    if (config.includeVideos) {
      xml += ' xmlns:video="http://www.google.com/schemas/sitemap-video/1.1"';
    }
    
    xml += '>\n';

    config.urls.forEach(url => {
      if (!url.loc.trim()) return;

      xml += '  <url>\n';
      
      // Construct full URL
      const fullUrl = url.loc.startsWith('http') 
        ? url.loc 
        : `${config.domain.replace(/\/$/, '')}${url.loc.startsWith('/') ? url.loc : `/${url.loc}`}`;
      
      xml += `    <loc>${escapeXml(fullUrl)}</loc>\n`;
      
      if (url.lastmod) {
        xml += `    <lastmod>${url.lastmod}</lastmod>\n`;
      }
      
      if (url.changefreq) {
        xml += `    <changefreq>${url.changefreq}</changefreq>\n`;
      }
      
      if (url.priority) {
        xml += `    <priority>${url.priority}</priority>\n`;
      }
      
      xml += '  </url>\n';
    });

    xml += '</urlset>';

    setGeneratedSitemap(xml);
    toast.success('XML sitemap generated successfully');
  };

  // Escape XML characters
  const escapeXml = (text: string): string => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    if (!generatedSitemap) {
      toast.error('No sitemap to copy. Please generate it first.');
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedSitemap);
      toast.success('Sitemap copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy sitemap');
    }
  };

  // Download sitemap file
  const downloadSitemap = () => {
    if (!generatedSitemap) {
      toast.error('No sitemap to download. Please generate it first.');
      return;
    }

    const blob = new Blob([generatedSitemap], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Sitemap downloaded successfully');
  };

  // Validate URLs
  const validateUrls = () => {
    const invalidUrls = config.urls.filter(url => {
      if (!url.loc.trim()) return true;
      const priority = parseFloat(url.priority);
      return priority < 0 || priority > 1;
    });

    if (invalidUrls.length > 0) {
      toast.error(`Found ${invalidUrls.length} invalid URL(s). Check empty URLs and priority values (0.0-1.0).`);
      return false;
    }

    return true;
  };

  // Auto-set priorities based on URL depth
  const autoSetPriorities = () => {
    setConfig(prev => ({
      ...prev,
      urls: prev.urls.map(url => {
        const depth = (url.loc.match(/\//g) || []).length;
        let priority = '0.8';
        
        if (url.loc === '/' || url.loc === '') priority = '1.0';
        else if (depth <= 2) priority = '0.9';
        else if (depth <= 3) priority = '0.8';
        else if (depth <= 4) priority = '0.7';
        else priority = '0.6';
        
        return { ...url, priority };
      })
    }));
    
    toast.success('Priorities auto-assigned based on URL depth');
  };

  const changefreqOptions = [
    'always', 'hourly', 'daily', 'weekly', 'monthly', 'yearly', 'never'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Map className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">XML Sitemap Generator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Create XML sitemaps to help search engines discover and index your website content. Customize priorities, change frequencies, and include multimedia content for optimal SEO.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Quick Actions */}
          <div className="flex gap-3 justify-center mb-8 flex-wrap">
            <button
              onClick={generateCommonPages}
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Globe className="w-4 h-4" />
              Add Common Pages
            </button>
            <button
              onClick={autoSetPriorities}
              className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
            >
              <Settings className="w-4 h-4" />
              Auto-Set Priorities
            </button>
            <button
              onClick={() => validateUrls() && generateSitemap()}
              className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Map className="w-4 h-4" />
              Generate Sitemap
            </button>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Configuration */}
            <div className="space-y-6">
              {/* Domain Configuration */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Domain Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Domain URL *
                    </label>
                    <input
                      type="url"
                      value={config.domain}
                      onChange={(e) => setConfig(prev => ({ ...prev, domain: e.target.value }))}
                      placeholder="https://example.com"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Default Change Frequency
                      </label>
                      <select
                        value={config.defaultChangefreq}
                        onChange={(e) => setConfig(prev => ({ ...prev, defaultChangefreq: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        {changefreqOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Default Priority
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={config.defaultPriority}
                        onChange={(e) => setConfig(prev => ({ ...prev, defaultPriority: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={config.includeImages}
                        onChange={(e) => setConfig(prev => ({ ...prev, includeImages: e.target.checked }))}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include image namespace</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={config.includeVideos}
                        onChange={(e) => setConfig(prev => ({ ...prev, includeVideos: e.target.checked }))}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include video namespace</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Bulk URL Addition */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">Bulk Add URLs</h3>
                <div className="space-y-4">
                  <textarea
                    value={bulkUrls}
                    onChange={(e) => setBulkUrls(e.target.value)}
                    placeholder="Enter URLs one per line:&#10;/about&#10;/contact&#10;/services&#10;/blog/post-1"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows={6}
                  />
                  <button
                    onClick={addBulkUrls}
                    disabled={!bulkUrls.trim()}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Add URLs
                  </button>
                </div>
              </div>

              {/* Individual URLs */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">URL Configuration</h3>
                  <button
                    onClick={addUrl}
                    className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add URL
                  </button>
                </div>

                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {config.urls.map((url, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gray-50 rounded-lg p-4"
                    >
                      <div className="grid gap-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              URL Path
                            </label>
                            <input
                              type="text"
                              value={url.loc}
                              onChange={(e) => updateUrl(index, 'loc', e.target.value)}
                              placeholder="/page-url"
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                          </div>
                          {config.urls.length > 1 && (
                            <button
                              onClick={() => removeUrl(index)}
                              className="text-red-600 hover:text-red-700 mt-6"
                              title="Remove URL"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>

                        <div className="grid md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Last Modified
                            </label>
                            <input
                              type="date"
                              value={url.lastmod}
                              onChange={(e) => updateUrl(index, 'lastmod', e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Change Frequency
                            </label>
                            <select
                              value={url.changefreq}
                              onChange={(e) => updateUrl(index, 'changefreq', e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            >
                              {changefreqOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Priority
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="1"
                              step="0.1"
                              value={url.priority}
                              onChange={(e) => updateUrl(index, 'priority', e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Generated Output */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Generated Sitemap</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={copyToClipboard}
                      disabled={!generatedSitemap}
                      className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                      Copy
                    </button>
                    <button
                      onClick={downloadSitemap}
                      disabled={!generatedSitemap}
                      className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg border p-4 h-96 overflow-y-auto">
                  {generatedSitemap ? (
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                      {generatedSitemap}
                    </pre>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <Map className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>Configure your URLs and click "Generate Sitemap" to see the XML output</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Sitemap Statistics */}
              {generatedSitemap && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-blue-50 rounded-lg p-6"
                >
                  <h4 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Sitemap Statistics
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
                    <div>
                      <div><strong>Total URLs:</strong> {config.urls.length}</div>
                      <div><strong>File Size:</strong> {new Blob([generatedSitemap]).size} bytes</div>
                    </div>
                    <div>
                      <div><strong>High Priority URLs:</strong> {config.urls.filter(u => parseFloat(u.priority) >= 0.8).length}</div>
                      <div><strong>Recently Updated:</strong> {config.urls.filter(u => {
                        const daysDiff = Math.floor((Date.now() - new Date(u.lastmod).getTime()) / (1000 * 60 * 60 * 24));
                        return daysDiff <= 7;
                      }).length}</div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Next Steps */}
              {generatedSitemap && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-50 rounded-lg p-6"
                >
                  <h4 className="font-semibold text-green-900 mb-3">Next Steps</h4>
                  <ol className="text-sm text-green-800 space-y-2 list-decimal list-inside">
                    <li>Download the sitemap.xml file</li>
                    <li>Upload it to your website's root directory</li>
                    <li>Add sitemap URL to your robots.txt file</li>
                    <li>Submit to Google Search Console</li>
                    <li>Submit to Bing Webmaster Tools</li>
                    <li>Update regularly when content changes</li>
                  </ol>
                </motion.div>
              )}
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Sitemap Best Practices</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>File Location:</strong> Place sitemap.xml in root directory</li>
                <li>• <strong>URL Limit:</strong> Maximum 50,000 URLs per sitemap</li>
                <li>• <strong>File Size:</strong> Keep under 50MB uncompressed</li>
                <li>• <strong>Update Frequency:</strong> Match change frequency to actual updates</li>
                <li>• <strong>Priority Values:</strong> Use relative priorities (0.0-1.0)</li>
                <li>• <strong>Submit to Search Engines:</strong> Use webmaster tools</li>
              </ul>
            </div>

            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3">Priority Guidelines</h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• <strong>1.0:</strong> Homepage and most important pages</li>
                <li>• <strong>0.8-0.9:</strong> Category pages, main sections</li>
                <li>• <strong>0.6-0.7:</strong> Product pages, blog posts</li>
                <li>• <strong>0.4-0.5:</strong> Archive pages, older content</li>
                <li>• <strong>0.1-0.3:</strong> Less important pages, legal pages</li>
                <li>• <strong>Relative Priority:</strong> Only relative to your own site</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SitemapGenerator;
